import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { schedule } from '@/mocks/schedules';

export default function ScheduleSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal-item');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 100}ms`;
            target.classList.add('reveal-visible');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="reveal-item reveal font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 leading-tight">
            {t('home.schedule.title')}
          </h2>
          <p className="reveal-item reveal font-body text-base text-foreground-600 mt-4">
            {t('home.schedule.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="reveal-item reveal bg-foreground-950 rounded-lg p-6 md:p-8 text-center">
            <p className="font-heading text-2xl md:text-3xl text-primary-500">
              {t('home.schedule.weekday')}
            </p>
            <p className="font-heading text-4xl md:text-5xl text-background-50 mt-2">
              {t('home.schedule.weekdayHours')}
            </p>
          </div>
          <div className="reveal-item reveal bg-background-50 border border-background-200 rounded-lg p-6 md:p-8 text-center">
            <p className="font-heading text-2xl md:text-3xl text-foreground-500">
              {t('home.schedule.saturday')}
            </p>
            <p className="font-heading text-4xl md:text-5xl text-foreground-950 mt-2">
              {t('home.schedule.saturdayHours')}
            </p>
          </div>
          <div className="reveal-item reveal bg-foreground-950 rounded-lg p-6 md:p-8 text-center">
            <p className="font-heading text-2xl md:text-3xl text-secondary-500">
              {t('home.schedule.sunday')}
            </p>
            <p className="font-heading text-4xl md:text-5xl text-background-50 mt-2">
              {t('home.schedule.sundayHours')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}